import { isLocalService, shouldForce401, shouldUseLocalBackend } from './localRequestUtils';
import { createCombinedAbortSignal } from './abortSignalUtils';
import { getGlobal } from './apiClients/global';
import { createChirpFetch, loadBalancers } from 'chirp-fetch';
import { getAvroGateway, ChirpInternalError } from './chirp-avro';
const TOOLS_DOMAIN_REGEX = /^(?:.*)tools(?:-[^.]+)?\.hubteam/;
function getInternalLoadBalancer() {
  var _location$href, _location;
  const location = (_location$href = (_location = getGlobal().location) === null || _location === void 0 ? void 0 : _location.href) !== null && _location$href !== void 0 ? _location$href : '';
  return TOOLS_DOMAIN_REGEX.test(location) ? loadBalancers.TOOLS : loadBalancers.PRIVATEHUBTEAM;
}
let rpcFetcherCache;
function getRpcFetcher(auth, env, hublet) {
  if (!rpcFetcherCache) {
    rpcFetcherCache = new Map();
  }
  const isAppAuth = auth === 'app' || auth === 'external';
  const loadBalancer = isAppAuth ? loadBalancers.APP : getInternalLoadBalancer();
  const cacheKey = `${auth}:${env !== null && env !== void 0 ? env : ''}:${hublet !== null && hublet !== void 0 ? hublet : ''}:${loadBalancer}`;
  if (!rpcFetcherCache.has(cacheKey)) {
    rpcFetcherCache.set(cacheKey, createChirpFetch({
      loadBalancer,
      gateway: auth,
      env: env,
      hublet
    }));
  }
  return rpcFetcherCache.get(cacheKey);
}
export async function makeChirpAvroCall({
  hubHttpClient,
  details,
  responseParser,
  inputs,
  alternateData,
  calledFrom = 'bend'
}) {
  var _inputs$request;
  const {
    timeout,
    portalId,
    userId,
    environment,
    hublet
  } = inputs !== null && inputs !== void 0 ? inputs : {};
  const request = (_inputs$request = inputs === null || inputs === void 0 ? void 0 : inputs.request) !== null && _inputs$request !== void 0 ? _inputs$request : {};
  const {
    auth,
    serviceName,
    rpcName,
    fingerprint,
    na1Only,
    isHublessAppAuth,
    isLocal
  } = details;
  const syncStackError = new Error(`CHIRP RPC failed for ${rpcName}`);
  const clientTimeout = typeof timeout === 'number' ? timeout : 5000;
  const shouldMakeLocalRequest = isLocal || shouldUseLocalBackend(isLocal) && (await isLocalService(serviceName));
  const headers = {};
  if (fingerprint !== undefined) {
    headers['x-hs-fingerprint'] = fingerprint;
  }
  if (portalId !== undefined && (auth === 'internal' || calledFrom === 'npmShim')) {
    headers['X-Origin-HubId'] = String(portalId);
  }
  if (userId !== undefined) {
    headers['X-Origin-UserId'] = String(userId);
  }
  const gatewayUrl = `${getAvroGateway({
    auth,
    timeout: clientTimeout,
    isLocal: shouldMakeLocalRequest,
    na1Only,
    isHublessAppAuth
  })}/${serviceName}/${rpcName}`;
  const makeHubHttpRequest = () => hubHttpClient.post(gatewayUrl, Object.assign({
    data: request,
    headers,
    timeout: clientTimeout,
    query: {
      clienttimeout: String(clientTimeout)
    },
    environment,
    hublet
  }, portalId && auth === 'app' && calledFrom !== 'npmShim' ? {
    portalId
  } : {})).catch(errorResponse => {
    if (errorResponse.status !== 0 && 'responseJSON' in errorResponse) {
      const isAuthError = errorResponse.status === 401;
      return Object.assign({}, isAuthError ? {
        isAuthError: true
      } : {}, errorResponse.responseJSON);
    }
    throw errorResponse;
  });
  const makeChirpFetchRequest = () => {
    const fetcher = getRpcFetcher(auth, environment, hublet);
    const signal = createCombinedAbortSignal(clientTimeout);
    return fetcher(`${gatewayUrl}?clienttimeout=${clientTimeout}`, {
      method: 'POST',
      headers: Object.assign({
        'Content-Type': 'application/json'
      }, headers),
      body: JSON.stringify(request),
      signal
    }).then(async response => {
      if (response.status === 401 || shouldForce401()) {
        throw Object.assign(new ChirpInternalError('Unauthorized'), {
          isAuthError: true
        });
      }
      const json = await response.json();
      return json;
    });
  };
  const data = alternateData !== null && alternateData !== void 0 ? alternateData : hubHttpClient ? makeHubHttpRequest() : makeChirpFetchRequest();
  return data.then(response => responseParser(response)).catch(error => {
    syncStackError.cause = error;
    throw syncStackError;
  });
}